import { HTMLContainer, Rectangle2d, TLResizeInfo, Vec, useValue } from "@tldraw/tldraw";
import React from "react";
import { mixColors } from "./CustomCloudUtil";
import { HOVER_EFFECT } from "./constants";
import { ShapeShadowDefs, createDefaultShadowV2, buildFilterRegion } from "./shadowUtils";
import { areCornersUniform, cornerRadiiCss, getCornerRadii } from "./utils/cornerRadii";

type colorMixType = {
  [key: string]: {
    color: string;
    isHidden: boolean;
  };
};
type outlineColorMixType = {
  [key: string]: {
    color: string;
    isHidden: boolean;
  };
};

export class ShapeUtilHelper {
  constructor(private editor: any) {}

  // Safe props access helper to prevent crashes
  getSafeProps(shape: any) {
    const props = shape?.props || {};
    return {
      // Basic dimensions
      w: props.w || 100,
      h: props.h || 100,

      // Colors and styling
      fill: props.fill || "#e3e3e3",
      outlineColor: props.outlineColor || "#000000",
      color: props.color || "#000000",

      // Stroke and border
      strokeWidth: props.strokeWidth || 0,
      strokeAlign: props.strokeAlign || "inside",
      borderWidth: props.borderWidth || 0,
      borderColor: props.borderColor || "#000000",
      borderSolid: props.borderSolid || "solid",

      // Dash and transparency
      dash: props.dash || "solid",
      transparency: props.transparency || 1,

      // Border visibility
      isShowBorderTop: props.isShowBorderTop !== undefined ? props.isShowBorderTop : true,
      isShowBorderBottom: props.isShowBorderBottom !== undefined ? props.isShowBorderBottom : true,
      isShowBorderLeft: props.isShowBorderLeft !== undefined ? props.isShowBorderLeft : true,
      isShowBorderRight: props.isShowBorderRight !== undefined ? props.isShowBorderRight : true,

      // Border styles
      borderTopStyle: props.borderTopStyle || "solid",
      borderBottomStyle: props.borderBottomStyle || "solid",
      borderLeftStyle: props.borderLeftStyle || "solid",
      borderRightStyle: props.borderRightStyle || "solid",

      // Effects
      filterBlur: props.filterBlur || 0,
      isCreatedFilterBlur: props.isCreatedFilterBlur || false,
      filterBlurValueWhenHidden: props.filterBlurValueWhenHidden || 0,
      isHiddenLayerBlur: props.isHiddenLayerBlur || false,
      isHiddenBorder: props.isHiddenBorder || false,

      // Scale and dimensions
      scale: props.scale || 1,
      rounded: props.rounded || 0,

      // Arrays with defaults
      outShadowArray: props.outShadowArray || {},
      inShadowArray: props.inShadowArray || {},
      colorMix: props.colorMix || ({ "0": { color: props.fill || "#e3e3e3", isHidden: false } } as colorMixType),
      outlineColorMix: props.outlineColorMix || ({} as outlineColorMixType),

      // Text specific (with defaults)
      text: props.text || "",
      size: props.size || 16,
      font: props.font || "sans",
      textAlign: props.textAlign || "start",
      verticalAlign: props.verticalAlign || "middle",
      autoSize: props.autoSize !== undefined ? props.autoSize : true,
      fixedHeight: props.fixedHeight || false,
      fontWeight: props.fontWeight || 400,
      underline: props.underline || "none",
      lineThrough: props.lineThrough || "none",
      italic: props.italic || "normal",
      textJustify: props.textJustify || "none",
      lineHeight: props.lineHeight || 1,
      letterSpacing: props.letterSpacing || 1,

      // Image specific
      assetId: props.assetId || null,
      playing: props.playing || false,
      url: props.url || "",
      crop: props.crop || null,
      flipX: props.flipX || false,
      flipY: props.flipY || false,
      flipH: props.flipH || false,
      flipV: props.flipV || false,

      // State flags
      isShowIndicator: props.isShowIndicator || false,
      isScale: props.isScale || false,
      isCreatedBorderItem: props.isCreatedBorderItem || false,
      forAbilityEditable: props.forAbilityEditable || false,

      // Legacy support
      previousBorderColor: props.previousBorderColor || "#000000",
      strokeDasharray: props.strokeDasharray,
    };
  }

  addDefaultColorMix(shape: any) {
    const newColorId = Date.now().toString();
    const updatedShape = {
      ...shape,
      props: {
        ...shape.props,
        colorMix: {
          ...shape.props.colorMix,
          [newColorId]: { color: "#d3d3d333", isHidden: false },
        },
      },
    };
    this.editor.updateShape(updatedShape);
  }

  addDefaultOutShadow(shape: any) {
    const newShadow = createDefaultShadowV2("drop");
    const updatedShape = {
      ...shape,
      props: {
        ...shape.props,
        outShadowArray: {
          ...shape.props.outShadowArray,
          [newShadow.id]: newShadow,
        },
      },
    };

    this.editor.updateShape(updatedShape);
  }

  addDefaultInShadow(shape: any) {
    const newShadow = createDefaultShadowV2("inner");
    const updatedShape = {
      ...shape,
      props: {
        ...shape.props,
        inShadowArray: {
          ...shape.props.inShadowArray,
          [newShadow.id]: newShadow,
        },
      },
    };

    this.editor.updateShape(updatedShape);
  }

  resizeScaled(shape: any, info: TLResizeInfo<any>) {
    const { newPoint, initialBounds, initialShape, scaleX, scaleY } = info;
    const { nextWidth, nextHeight, x, y } = this.calculateNewDimensions(newPoint, initialBounds, scaleX, scaleY);

    return {
      id: shape.id,
      type: shape.type,
      x,
      y,
      props: {
        w: nextWidth / (initialShape.props.scale ?? 1),
        h: nextHeight / (initialShape.props.scale ?? 1),
      },
    };
  }

  resizeDefault(shape: any, info: TLResizeInfo<any>) {
    const { newPoint, initialBounds, scaleX, scaleY } = info;
    const { nextWidth, nextHeight, x, y } = this.calculateNewDimensions(newPoint, initialBounds, scaleX, scaleY);

    return {
      id: shape.id,
      type: shape.type,
      x,
      y,
      props: {
        w: nextWidth - shape.props.borderWidth * 2,
        h: nextHeight - shape.props.borderWidth * 2,
      },
    };
  }

  calculateNewDimensions(newPoint: any, initialBounds: any, scaleX: number, scaleY: number) {
    const nextWidth = Math.max(1, Math.abs(initialBounds.width * scaleX));
    const nextHeight = Math.max(1, Math.abs(initialBounds.height * scaleY));
    const x = scaleX < 0 ? newPoint.x - nextWidth : newPoint.x;
    const y = scaleY < 0 ? newPoint.y - nextHeight : newPoint.y;

    return { nextWidth, nextHeight, x, y };
  }

  calculateAdjustedDimensions(shape: any) {
    const props = this.getSafeProps(shape);
    const {
      h,
      w,
      borderWidth,
      strokeWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isHiddenBorder,
      scale,
    } = props;

    // Stroke width should not affect the shape's layout dimensions
    // Only borders and scale should affect the adjusted dimensions
    const adjustedHeight =
      h * (scale ?? 1) +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const adjustedWidth =
      w * (scale ?? 1) +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    return new Rectangle2d({
      width: adjustedWidth,
      height: adjustedHeight,
      isFilled: true,
    });
  }

  // NOTE: previously this class exposed `calculateColors` and `isShapeHovered`
  // as plain methods that internally called `useMemo` / `useValue`. That's
  // fragile — ESLint's `react-hooks/rules-of-hooks` rule keys off the `use*`
  // naming convention, so hook misuse inside class methods (early returns,
  // conditional calls, calls from `getBounds` / `toSvg` / event handlers)
  // would not be caught at build time. Hooks are now extracted as standalone
  // `use*` functions below the class — ESLint enforces the rules of hooks
  // on those by name.

  /**
   * Renders the SVG for path-based shapes (cloud, heart, star, ellipse, oval,
   * triangle). Named with the `use*` prefix because it composes three hooks
   * (`useCalculatedColors`, `useIsShapeHovered`, `useShapeFilterChain`) and
   * must therefore be called from a React render context — see Rules of
   * Hooks. ESLint's `react-hooks/rules-of-hooks` enforces this at the call
   * sites in each shape util's `component()` method.
   */
  useRenderedShape(
    shape: any,
    pathData: string,
    outlineColor: string,
    dashArray: string | undefined,
    shadowFilterId: string,
    outShadowArray: any,
    inShadowArray: any,
    indicator: (shape: any) => JSX.Element | null
  ) {
    const props = this.getSafeProps(shape);
    const { calculatedFill, calculatedOutlineColor } = useCalculatedColors(props.colorMix, props.outlineColorMix);
    const isHovered = useIsShapeHovered(this.editor, shape.id);

    // Check if shape has clip-path from mask group
    const maskClipPath = shape.meta?.maskClipPath;

    const {
      w,
      h,
      strokeWidth,
      strokeAlign,
      rounded,
      isHiddenLayerBlur,
      filterBlur,
      colorMix,
      borderWidth,
      isShowBorderTop,
      isShowBorderBottom,
      isShowBorderLeft,
      isShowBorderRight,
      isHiddenBorder,
      flipX,
      flipY,
    } = props;

    // Calculate adjusted dimensions for the hover indicator
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);
    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    // Build the actual filter URL chain from the visible shadows.
    // The `shadowFilterId` arg passed by callers (e.g. `shadow-${shape.id}`) is a stale
    // pre-array convention that no longer matches any defined filter id, since filters
    // are defined per-shadow with their own ids inside <ShapeShadowDefs>.
    const fullFilter = useShapeFilterChain(outShadowArray, inShadowArray, isHiddenLayerBlur, filterBlur, shape.id);

    // Calculate stroke positioning based on strokeAlign
    const getStrokeTransform = () => {
      const sw = strokeWidth || 0;
      switch (strokeAlign) {
        case "outside":
          // SVG expands to show stroke outside, use double stroke + mask
          return {
            viewBoxX: -sw,
            viewBoxY: -sw,
            viewBoxW: w + sw * 2,
            viewBoxH: h + sw * 2,
            svgWidth: w + sw * 2,
            svgHeight: h + sw * 2,
            svgTop: -sw,
            svgLeft: -sw,
            actualStrokeWidth: sw * 2,
            needsTransform: false,
          };
        case "center":
          // Default SVG behavior - stroke centered on path (half inside, half outside)
          return {
            viewBoxX: -sw / 2,
            viewBoxY: -sw / 2,
            viewBoxW: w + sw,
            viewBoxH: h + sw,
            svgWidth: w + sw,
            svgHeight: h + sw,
            svgTop: -sw / 2,
            svgLeft: -sw / 2,
            pathTranslateX: 0,
            pathTranslateY: 0,
            actualStrokeWidth: sw,
            needsTransform: false,
          };
        case "inside":
        default:
          // Use clipPath to show only inner half of stroke
          return {
            viewBoxX: 0,
            viewBoxY: 0,
            viewBoxW: w,
            viewBoxH: h,
            svgWidth: w,
            svgHeight: h,
            svgTop: 0,
            svgLeft: 0,
            actualStrokeWidth: sw,
            needsTransform: false,
          };
      }
    };
    const strokeTransform = getStrokeTransform();

    // Calculate flip transform style
    const getFlipStyle = () => {
      if (!flipX && !flipY) return {};
      const scale = `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`;
      const translate = `translate(${flipX ? w : 0}px, ${flipY ? h : 0}px)`;
      return {
        transform: `${translate} ${scale}`,
        transformOrigin: "0 0",
      };
    };

    return (
      <HTMLContainer id={shape.id} className="relative flex items-center justify-center">
        <div
          style={{
            width: w,
            height: h,
            clipPath: maskClipPath ? `path('${maskClipPath}')` : undefined,
            overflow: "visible",
            ...getFlipStyle(),
          }}
        >
          <svg
            style={{
              // Read per-corner overrides (`roundedTL/TR/BR/BL`) and fall
              // back to the uniform `rounded` when they're unset. When all
              // four corners match we keep the single-value form so we
              // don't regress on existing snapshots.
              borderRadius: (() => {
                const radii = getCornerRadii(shape.props);
                return areCornersUniform(radii) ? `${radii.tl}px` : cornerRadiiCss(radii);
              })(),
              overflow: "visible",
            }}
            viewBox={`0 0 ${w} ${h}`}
          >
            <defs>
              {/* ClipPath for inside stroke alignment */}
              {strokeAlign === "inside" && (
                <clipPath id={`clip-inside-${shape.id}`}>
                  <path d={pathData} />
                </clipPath>
              )}
              {/* Mask for outside stroke alignment - only show stroke outside the path */}
              {strokeAlign === "outside" && (
                <mask id={`mask-outside-${shape.id}`}>
                  <rect width="100%" height="100%" fill="white" />
                  <path d={pathData} fill="black" />
                </mask>
              )}
              {/* Gradient definition */}
              {Object.values(colorMix as colorMixType).filter((c) => !c.isHidden).length > 0 && (
                <linearGradient id={`gradient-${shape.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  {Object.values(colorMix as colorMixType)
                    .filter((c) => !c.isHidden)
                    .map((colorObj, index) => (
                      <stop
                        key={index}
                        offset={`${
                          (index / (Object.values(colorMix as colorMixType).filter((c) => !c.isHidden).length - 1)) *
                          100
                        }%`}
                        stopColor={colorObj.color}
                      />
                    ))}
                </linearGradient>
              )}
              <ShapeShadowDefs
                outShadowArray={outShadowArray}
                inShadowArray={inShadowArray}
                shapeWidth={w}
                shapeHeight={h}
              />

              {/* Blur filter definition. The region is sized to the
                  actual blur amount so heavy blurs don't get clipped to
                  a square at the legacy 200% region. */}
              {!isHiddenLayerBlur && filterBlur && filterBlur > 0 && (
                <filter id={`blur-${shape.id}`} {...buildFilterRegion({ blur: filterBlur }, w, h)}>
                  <feGaussianBlur stdDeviation={filterBlur} />
                </filter>
              )}
            </defs>
            {/* Invisible hitarea to capture pointer events regardless of opacity */}
            {strokeTransform.needsTransform ? (
              <g transform={`translate(${strokeTransform.pathTranslateX}, ${strokeTransform.pathTranslateY})`}>
                <path d={pathData} fill="transparent" stroke="none" style={{ pointerEvents: "all" }} />
              </g>
            ) : (
              <path d={pathData} fill="transparent" stroke="none" style={{ pointerEvents: "all" }} />
            )}
            {/* Group for shadow that includes both shape and stroke */}
            <g filter={fullFilter} style={{ pointerEvents: "none" }}>
              {/* Main shape */}
              <path d={pathData} fill={calculatedFill} stroke="none" strokeLinecap="round" />
              {/* Stroke overlay - rendered separately to not affect inner element */}
              {strokeWidth && strokeWidth > 0 && (
                <>
                  {strokeAlign === "outside" ? (
                    // For outside stroke: scale the path up so the stroke extends outward
                    // Scale factor: add strokeWidth*1.3 to dimensions to push stroke fully outside
                    <g
                      transform={`translate(${w / 2}, ${h / 2}) scale(${(w + strokeWidth) / w}, ${
                        (h + strokeWidth) / h
                      }) translate(${-w / 2}, ${-h / 2})`}
                    >
                      <path
                        d={pathData}
                        fill="none"
                        stroke={
                          Object.entries(shape.props.outlineColorMix).length > 0 ? calculatedOutlineColor : outlineColor
                        }
                        strokeWidth={strokeWidth}
                        strokeDasharray={dashArray}
                        strokeLinecap="butt"
                        strokeLinejoin="miter"
                      />
                    </g>
                  ) : (
                    // For inside/center: use normal rendering
                    <path
                      d={pathData}
                      fill="none"
                      stroke={
                        Object.entries(shape.props.outlineColorMix).length > 0 ? calculatedOutlineColor : outlineColor
                      }
                      strokeWidth={strokeAlign === "inside" ? strokeWidth * 2 : strokeTransform.actualStrokeWidth}
                      strokeDasharray={dashArray}
                      strokeLinecap="butt"
                      strokeLinejoin="miter"
                      clipPath={strokeAlign === "inside" ? `url(#clip-inside-${shape.id})` : undefined}
                    />
                  )}
                </>
              )}
            </g>
          </svg>
          {isHovered && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: adjustedWidth,
                height: adjustedHeight,
                pointerEvents: "none",
                zIndex: 999,
                opacity: 1,
                isolation: "isolate",
              }}
            >
              <svg
                width={adjustedWidth}
                height={adjustedHeight}
                viewBox={`0 0 ${adjustedWidth} ${adjustedHeight}`}
                style={{
                  fill: "none",
                  stroke: HOVER_EFFECT.COLOR,
                  strokeWidth: "2",
                  pointerEvents: "none",
                  opacity: 1,
                }}
              >
                {indicator(shape)}
              </svg>
            </div>
          )}
        </div>
      </HTMLContainer>
    );
  }
}

// Standalone hooks used by `ShapeUtilHelper.useRenderedShape` and the
// inline `component()` methods of the path-rendering shape utils. Named
// with the `use*` prefix so ESLint's `react-hooks/rules-of-hooks` rule
// catches misuse (conditional calls, calls from event handlers, calls
// from non-render methods like `toSvg` / `getBounds`) at lint time
// instead of at runtime.

/**
 * Memoized fill + outline colors for a shape. Cached on the input maps,
 * which tldraw preserves by reference under immutable updates, so dragging
 * N shapes (only `x` / `y` change) skips the
 * `Object.values().filter().map().mixColors()` chain on every pointer move.
 */
function useCalculatedColors(colorMix: colorMixType, outlineColorMix: outlineColorMixType) {
  return React.useMemo(() => {
    const visibleColors = Object.values(colorMix).filter((c) => !c.isHidden);
    const calculatedFill = visibleColors.length > 0 ? mixColors(visibleColors.map((c) => c.color)) : "transparent";

    const visibleOutlineColors = Object.values(outlineColorMix).filter((c) => !c.isHidden);
    const calculatedOutlineColor =
      visibleOutlineColors.length > 0 ? mixColors(visibleOutlineColors.map((c) => c.color)) : "transparent";

    return { calculatedFill, calculatedOutlineColor };
  }, [colorMix, outlineColorMix]);
}

/**
 * Boolean "is this shape currently hovered". Subscribes only to the derived
 * boolean (via tldraw's `useValue`) rather than to the full hovered-shape
 * record, so unrelated shapes don't re-render on every hover transition.
 */
function useIsShapeHovered(editor: any, shapeId: string): boolean {
  return useValue("isHovered", () => editor.getHoveredShapeId() === shapeId, [editor, shapeId]);
}

/**
 * Memoized SVG `filter=""` URL chain for a shape's visible drop / inner
 * shadows + layer blur. Cached on the source arrays + blur primitives so a
 * multi-shape drag doesn't re-walk the shadow maps per pointer move.
 */
function useShapeFilterChain(
  outShadowArray: any,
  inShadowArray: any,
  isHiddenLayerBlur: boolean,
  filterBlur: number,
  shapeId: string
): string | undefined {
  return React.useMemo(() => {
    const visibleOutShadowIds = outShadowArray
      ? Object.values(outShadowArray)
          .filter((s: any) => !s.isHidden)
          .map((s: any) => s.id)
      : [];
    const visibleInShadowIds = inShadowArray
      ? Object.values(inShadowArray)
          .filter((s: any) => !s.isHidden)
          .map((s: any) => s.id)
      : [];
    const shadowFilterChain = [...visibleOutShadowIds, ...visibleInShadowIds].map((id) => `url(#${id})`).join(" ");
    const blurFilterUrl = !isHiddenLayerBlur && filterBlur && filterBlur > 0 ? `url(#blur-${shapeId})` : "";
    return [shadowFilterChain, blurFilterUrl].filter(Boolean).join(" ") || undefined;
  }, [outShadowArray, inShadowArray, isHiddenLayerBlur, filterBlur, shapeId]);
}
